import { useTraduccion } from '@tallerya/i18n';
import { Boton, CampoAreaTexto, CampoSeleccion, Formulario, Seccion, useAvisos, useFormulario } from '@tallerya/ui';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useSucursal } from '../../sucursal/ProveedorSucursal';
import { SelectorDeCliente } from '../activos/SelectorDeCliente';
import { useAsignables, useCambiosOrden, useClaveDeReintento } from './datos';
import { ElegirVehiculo, type VehiculoElegido } from './ElegirVehiculo';

// El alta rápida de la orden (#131): **«llega un carro, se escribe la placa, ya
// hay orden»**. Es la pantalla que más vende el producto.
//
//   1. Se escribe la placa y se elige el carro; si no existe, se registra ahí
//      mismo (`ElegirVehiculo`). Su cliente pasa a ser el de la orden.
//   2. Lo que dice el cliente y a quién se asigna, los dos opcionales.
//   3. «Abrir orden» la abre en la sucursal del app bar y lleva a su ficha.
//
// «Abrir sin vehículo» cambia la placa por un cliente: el núcleo no asume
// vehículos, y una orden pide cliente o activo, no los dos.
//
// No hay frame en el Figma (`docs/figma.md`, «Lo que no tiene diseño»): sigue el
// molde de los formularios, con la ventana encima que se eligió al tomar el issue.

export function FormularioOrden() {
  const { t } = useTraduccion();
  useTituloPantalla(t('admin.ordenes.nueva.titulo'));
  const { actual, cargando } = useSucursal();

  if (cargando) return <p className="entrada">{t('app.cargando')}</p>;

  // La orden nace en una sucursal, y es la del app bar: sin ninguna no hay dónde.
  if (!actual) {
    return (
      <section className="aviso">
        <h2>{t('admin.ordenes.sinSucursal.titulo')}</h2>
        <p>{t('admin.ordenes.sinSucursal.texto')}</p>
      </section>
    );
  }

  // Cambiar de sucursal arriba empieza de nuevo: el personal que se ofrece es otro.
  return <Editor key={String(actual.id)} sucursal={{ id: String(actual.id), nombre: actual.name }} />;
}

interface Valores {
  assetId: string;
  customerId: string;
  reportedIssue: string;
  assignedTo: string;
}

function Editor({ sucursal }: { sucursal: { id: string; nombre: string } }) {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { avisar } = useAvisos();
  const cambios = useCambiosOrden();
  // Un doble clic o un reintento abren una sola orden (ver `useClaveDeReintento`).
  const claveDe = useClaveDeReintento();
  const abierta = useRef<string | null>(null);

  const [sinVehiculo, setSinVehiculo] = useState(false);
  const [vehiculo, setVehiculo] = useState<VehiculoElegido | null>(null);
  const [nombreCliente, setNombreCliente] = useState<string | null>(null);
  const asignables = useAsignables(sucursal.id);

  const form = useFormulario<Valores>({
    inicial: { assetId: '', customerId: '', reportedIssue: '', assignedTo: '' },
    // El mismo código que daría la API, para decirlo antes de enviar.
    validar: (v) =>
      !v.assetId && !v.customerId
        ? { [sinVehiculo ? 'customerId' : 'assetId']: 'service_order.sin_cliente_ni_activo' }
        : {},
    guardar: (v) => {
      const envio = {
        locationId: sucursal.id,
        customerId: v.customerId,
        assetId: v.assetId,
        reportedIssue: v.reportedIssue,
        assignedTo: v.assignedTo,
      };
      return cambios.abrir(claveDe(envio), envio, (id) => {
        abierta.current = id;
      });
    },
    camposPorCodigo: {
      'service_order.activo_no_encontrado': 'assetId',
      'service_order.cliente_no_encontrado': 'customerId',
      'service_order.personal_no_encontrado': 'assignedTo',
      'service_order.personal_apagado': 'assignedTo',
    },
  });
  const { cambiar } = form;

  /** El cliente de la orden es el del carro, si tiene: es lo que pasa casi siempre. */
  const elegir = useCallback(
    (elegido: VehiculoElegido) => {
      setVehiculo(elegido);
      cambiar('assetId', elegido.id);
      cambiar('customerId', elegido.clienteId ?? '');
      setNombreCliente(elegido.clienteNombre);
    },
    [cambiar],
  );

  function quitarVehiculo() {
    setVehiculo(null);
    cambiar('assetId', '');
    cambiar('customerId', '');
    setNombreCliente(null);
  }

  function alternarSinVehiculo() {
    quitarVehiculo();
    setSinVehiculo(!sinVehiculo);
  }

  function guardado() {
    avisar({ titulo: t('admin.ordenes.aviso.abierta') });
    if (abierta.current) void navegar(`/ordenes/${abierta.current}`);
  }

  // Sin carro, o con un carro que no tiene cliente, el cliente se elige aquí.
  const eligeCliente = sinVehiculo || (vehiculo !== null && vehiculo.clienteId === null);

  return (
    <Formulario
      form={form}
      titulo={t('admin.ordenes.nueva.titulo')}
      info={t('admin.ordenes.nueva.info')}
      descripcion={t('admin.ordenes.nueva.enSucursal', { sucursal: sucursal.nombre })}
      etiquetaGuardar={t('admin.ordenes.nueva.abrir')}
      onGuardado={guardado}
      onCancelar={() => void navegar('/')}
    >
      {!sinVehiculo && (
        <Seccion titulo={t('admin.ordenes.seccion.vehiculo')} info={t('admin.ordenes.seccion.vehiculoTexto')}>
          <ElegirVehiculo
            elegido={vehiculo}
            onElegir={elegir}
            onQuitar={quitarVehiculo}
            error={form.errores.assetId}
            id={form.idDe('assetId')}
          />
        </Seccion>
      )}

      {eligeCliente && (
        <Seccion
          titulo={t('admin.ordenes.seccion.cliente')}
          info={sinVehiculo ? t('admin.ordenes.seccion.clienteSinVehiculo') : t('admin.ordenes.seccion.clienteOpcional')}
        >
          <SelectorDeCliente
            valor={form.valores.customerId}
            nombre={nombreCliente}
            error={form.errores.customerId}
            id={form.idDe('customerId')}
            onElegir={(id, nombre) => {
              cambiar('customerId', id);
              setNombreCliente(nombre);
            }}
            onQuitar={() => {
              cambiar('customerId', '');
              setNombreCliente(null);
            }}
          />
        </Seccion>
      )}

      <div className="orden-alta__alternar">
        <Boton variante="texto" onClick={alternarSinVehiculo}>
          {sinVehiculo ? t('admin.ordenes.conVehiculo') : t('admin.ordenes.sinVehiculo')}
        </Boton>
      </div>

      <Seccion titulo={t('admin.ordenes.seccion.trabajo')}>
        <CampoAreaTexto
          form={form}
          nombre="reportedIssue"
          etiqueta={t('admin.ordenes.fallaReportada')}
          info={t('admin.ordenes.fallaReportada.info')}
          ancho="completo"
        />
        <CampoSeleccion
          form={form}
          nombre="assignedTo"
          etiqueta={t('admin.ordenes.asignarA')}
          placeholder={t('admin.ordenes.sinAsignar')}
          opciones={(asignables.data ?? []).map((p) => ({ valor: String(p.id), etiqueta: p.name }))}
        />
      </Seccion>
    </Formulario>
  );
}
